import React, { useMemo, useState } from "react";
import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import {
  tokens,
  StatusPill,
  CodeChip,
  PatientStrip,
  ClosedLoopTracker,
  SectionPanel,
  ClinicalEmptyState,
} from "../../shared/clinical-design";
import { CHECKLIST_STEPS, STAGE_META, stageFor } from "../common/constants";

const LABEL_SX = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.05em",
  textTransform: "uppercase",
  color: tokens.ink3,
};
const VALUE_SX = {
  fontFamily: tokens.fontMono,
  fontVariantNumeric: "tabular-nums",
  fontSize: 14,
  color: tokens.ink,
};

function Field({ label, children }) {
  return (
    <Box>
      <Typography sx={LABEL_SX}>{label}</Typography>
      <Typography component="div" sx={VALUE_SX}>{children ?? "—"}</Typography>
    </Box>
  );
}

/**
 * Detalle del nuevo miembro: journey tracker (closed-loop) + checklist con
 * acción "mark complete" (estado local hasta que exista el backend).
 */
export default function OnboardingDetailPanel({ row, locationLabel }) {
  // Checklist editable localmente; se reinicia por miembro vía key del padre.
  const [checklist, setChecklist] = useState(row?.checklist || {});

  const stage = useMemo(() => stageFor(checklist), [checklist]);

  if (!row) {
    return (
      <SectionPanel title="Member detail">
        <ClinicalEmptyState message="Select a new member to review the onboarding journey." />
      </SectionPanel>
    );
  }

  const stageMeta = STAGE_META[stage];
  const firstPendingIdx = CHECKLIST_STEPS.findIndex((s) => !checklist[s.key]);

  const loopSteps = CHECKLIST_STEPS.map((s, i) => ({
    label: s.label,
    state: checklist[s.key] ? "done" : i === firstPendingIdx ? "active" : "pending",
  }));

  const markDone = (key) => setChecklist((prev) => ({ ...prev, [key]: true }));

  return (
    <Stack spacing={1.5}>
      <PatientStrip
        patient={{
          name: row.name,
          age: row.age,
          gender: row.gender,
          cipId: row.cipId,
          location: locationLabel,
          pcp: row.pcpName,
          membership: row.plan,
        }}
        actions={<StatusPill tone={stageMeta.tone} label={stageMeta.label} />}
      />

      <SectionPanel title="Onboarding journey">
        <ClosedLoopTracker steps={loopSteps} />
        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap", rowGap: 1 }}>
          <StatusPill
            tone={row.daysSinceEnrollment > 60 ? "crit" : row.daysSinceEnrollment > 30 ? "warn" : "info"}
            label={`Day ${row.daysSinceEnrollment} of 90-day AWV window`}
            dense
          />
          <StatusPill tone="muted" label={`Enrolled ${row.enrollmentDate}`} dense />
        </Stack>
      </SectionPanel>

      <SectionPanel title="Checklist">
        <Stack spacing={0.75}>
          {CHECKLIST_STEPS.map((s, i) => {
            const done = checklist[s.key];
            const isNext = i === firstPendingIdx;
            return (
              <Stack
                key={s.key}
                direction="row"
                alignItems="center"
                spacing={1.5}
                sx={{
                  px: 1.25,
                  py: 0.75,
                  bgcolor: done ? tokens.okBg : tokens.surface2,
                  border: `1px solid ${isNext ? tokens.primary : tokens.line}`,
                  borderRadius: "4px",
                }}
              >
                <Typography
                  sx={{ fontSize: 13, color: done ? tokens.ok : tokens.ink, fontWeight: isNext ? 600 : 400 }}
                >
                  {done ? "✓" : "○"} {s.label}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {done ? (
                  <StatusPill tone="ok" label="Completed" dense />
                ) : (
                  <Button
                    size="small"
                    variant={isNext ? "contained" : "outlined"}
                    disableElevation
                    onClick={() => markDone(s.key)}
                    sx={{
                      textTransform: "none",
                      fontSize: 12.5,
                      ...(isNext
                        ? { bgcolor: tokens.primary, "&:hover": { bgcolor: tokens.primary700 } }
                        : { color: tokens.primary, borderColor: tokens.primary }),
                    }}
                  >
                    Mark complete
                  </Button>
                )}
              </Stack>
            );
          })}
        </Stack>
        <Typography sx={{ fontSize: 11.5, color: tokens.ink3, mt: 1 }}>
          Checklist changes are local-only in this preview — they will persist to
          outreach.TAB_ONBOARDING once the backend endpoint is available.
        </Typography>
      </SectionPanel>

      <SectionPanel title="Contact & coverage">
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Field label="Phone">{row.phone || "—"}</Field>
          </Grid>
          <Grid size={{ xs: 6, md: 5 }}>
            <Field label="Email">
              <Box component="span" sx={{ ...VALUE_SX, fontSize: 12.5, wordBreak: "break-all" }}>
                {row.email || "—"}
              </Box>
            </Field>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Field label="Plan">
              <CodeChip code={row.plan} />
            </Field>
          </Grid>
          <Grid size={{ xs: 6, md: 2 }}>
            <Field label="Contactable">
              {row.phone || row.email ? (
                <StatusPill tone="ok" label="Yes" dense />
              ) : (
                <StatusPill tone="crit" label="No" dense />
              )}
            </Field>
          </Grid>
        </Grid>
      </SectionPanel>
    </Stack>
  );
}
